<template>
  <div class="card shadow">
    <div
      class="card-header border-0"
    >
      <div class="row align-items-center">
        <div class="col">
          <h3 class="mb-0">
            Analysis Request Platform NMR
          </h3>
        </div>
        <div class="col text-right">
          <button class="btn btn-icon btn-success btn-sm" @click="showCreateModal()">
              <span class="btn-inner--icon"><i class="fa fa-plus"></i></span>
              <span class="btn-inner--text">Add Analysis Request</span>
          </button>
          <button class="btn btn-icon btn-info btn-sm" @click="exportAll()" v-if="getUser.isAdmin">
              <span class="btn-inner--icon"><i class="fa fa-download"></i></span>
              <span class="btn-inner--text">Export</span>
          </button>
          <el-tooltip placement="top" content="Refresh">
            <button class="btn btn-icon btn-default btn-sm" @click="reloadData()">
                <span class="btn-inner--icon"><i class="fa fa-sync"></i></span>
            </button>
          </el-tooltip>
        </div>
      </div>

      <div class="row mt-3">
          <div class="col">
            <label for="filter_device" class="small">Instruments</label>
            <select id="filter_device" 
              class="form-control form-control-sm" 
              v-on:change="reloadData()"
              v-model="filters.device">
              <option value="">-</option>
              <option v-for="device in enabledResources(devices)" :key="device.id" v-bind:value="device['id']">{{device.name}}</option> 
            </select>
          </div>

          <div class="col">
            <label for="filter_status" class="small">Status</label>
            <select id="filter_status" 
              class="form-control form-control-sm" 
              v-on:change="reloadData()"
              v-model="filters.status">
              <option value="">-</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="processing">Processing</option>
              <option value="done">Done</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div class="col">
            <label for="filter_users" class="small">Users</label>
            <select id="filter_users" 
              class="form-control form-control-sm" 
              v-on:change="reloadData()"
              v-model="filters.user">
                <option value="">-</option>
                <option v-for="user in enabledResources(users)" :key="user.id" v-bind:value="user['id']">{{user.fullName}}</option> 
            </select>
          </div>
      </div>

      <!-- Filtres d'export -->
      <div class="row mt-3">
          <div class="col-md-4">
            <label for="export_start_date" class="small">Date de début</label>
            <input type="date" id="export_start_date" 
              class="form-control form-control-sm" 
              v-model="exportFilters.startDate">
          </div>

          <div class="col-md-4">
            <label for="export_end_date" class="small">Date de fin</label>
            <input type="date" id="export_end_date" 
              class="form-control form-control-sm" 
              v-model="exportFilters.endDate">
          </div>

          <div class="col-md-4">
            <label for="export_group" class="small">Groupe</label>
            <select id="export_group" 
              class="form-control form-control-sm" 
              v-model="exportFilters.groupId">
                <option value="">Tous les groupes</option>
                <option v-for="group in groups" :key="group.id" v-bind:value="group.id">{{group.name}}</option> 
            </select>
          </div>
      </div>

      <base-alert type="success" class="mt-3" v-if="successMessage">
        {{ successMessage ? 'Export réussi !' : 'Analysis has been successfully added.' }}
      </base-alert>
      <base-alert type="danger" class="mt-3" v-if="errorMessage">
        {{ errorMessage }}
      </base-alert>
    </div>



    <div class="table-responsive">
      <base-table
        class="table align-items-center table-flush"
        thead-classes="thead-light"
        tbody-classes="list"
        :data="items"
      >
        <template v-slot:columns>
          <th style="cursor: pointer;" @click="sortBy('sampleName')" :class="{ 'text-dark font-weight-bold' : filters['order[sampleName]']}">
            Sample Name
            <i v-if="filters['order[sampleName]'] === 'asc'" class="fa fa-arrow-up"></i>
            <i v-if="filters['order[sampleName]'] === 'desc'" class="fa fa-arrow-down"></i>
          </th>
          <th style="cursor: pointer;" @click="sortBy('user.fullName')" :class="{ 'text-dark font-weight-bold' : filters['order[user.fullName]']}">
            User
            <i v-if="filters['order[user.fullName]'] === 'asc'" class="fa fa-arrow-up"></i>
            <i v-if="filters['order[user.fullName]'] === 'desc'" class="fa fa-arrow-down"></i>
          </th>
          <th>Instrument</th>
          <th>Status</th>
          <th style="cursor: pointer;" @click="sortBy('submittedAt')" :class="{ 'text-dark font-weight-bold' : filters['order[submittedAt]']}">
            Submitted
            <i v-if="filters['order[submittedAt]'] === 'asc'" class="fa fa-arrow-up"></i>
            <i v-if="filters['order[submittedAt]'] === 'desc'" class="fa fa-arrow-down"></i>
          </th>
          <th style="cursor: pointer;" @click="sortBy('endAt')" :class="{ 'text-dark font-weight-bold' : filters['order[endAt]']}">
            Done
            <i v-if="filters['order[endAt]'] === 'asc'" class="fa fa-arrow-up"></i>
            <i v-if="filters['order[endAt]'] === 'desc'" class="fa fa-arrow-down"></i>
          </th>
          <th></th>
        </template>

        <template v-slot:default="row">
          <th scope="row">
           {{ row.item.sampleName }}
          </th>
          <td>
            {{ row.item.user.fullName }}
          </td>
          <td>
            {{ row.item.device.name }}
          </td>

          <td>
            <badge class="mr-4" :type="itemStatusType(row.item)">
              <span class="status">{{ itemStatusTypeStr(row.item) }}</span>
            </badge>
          </td>

          <td>
            {{dateFormat(row.item.submittedAt)}}
          </td>

          <td>
            {{dateFormat(row.item.endAt)}}
          </td>

          <td class="text-right">

           <el-tooltip placement="top" content="Edit"  v-if="showEditBtn(row.item)" >
              <button class="btn btn-icon btn-primary btn-sm" @click="showEditModal(row.item)">
                <span class="btn-inner--icon"><i class="fa fa-edit"></i></span>
              </button>
           </el-tooltip>

            <el-tooltip placement="top" content="View">
              <button class="btn btn-icon btn-primary btn-sm" @click="showItem(row.item)">
                <span class="btn-inner--icon"><i class="fa fa-eye"></i></span>
              </button>
            </el-tooltip>

            <el-tooltip placement="top" content="Export this request" v-if="getUser.isAdmin && row.item.status == 'submitted'">
              <button class="btn btn-icon btn-default btn-sm" @click="uploadCSV(row.item)">
                <span class="btn-inner--icon"><i class="fa fa-download"></i></span>
              </button>
            </el-tooltip>

            <el-tooltip placement="top" content="Export all user requests" v-if="getUser.isAdmin && row.item.status == 'submitted'">
              <button class="btn btn-icon btn-default btn-sm" @click="uploadAllCSV(row.item)">
                <span class="btn-inner--icon"><i class="fa fa-file-archive"></i></span>
              </button>
            </el-tooltip>

            <el-tooltip placement="top" content="Cancel" v-if="getUser.isAdmin && ['submitted','processing'].includes(row.item.status)">
              <button class="btn btn-icon btn-danger btn-sm" @click="cancelAnalysis(row.item)">
                <span class="btn-inner--icon"><i class="fa fa-times"></i></span>
              </button>
            </el-tooltip>

            <el-tooltip placement="top" content="Done" v-if="getUser.isAdmin && row.item.status == 'processing'">
              <button class="btn btn-icon btn-success btn-sm" @click="doneAnalysis(row.item)">
                <span class="btn-inner--icon"><i class="fa fa-check"></i></span>
              </button>
            </el-tooltip>
          </td>
        </template>
      </base-table>
    </div>

    <div class="card-footer d-flex justify-content-end">
      <base-pagination :total="totalItems" perPage=20 v-model="currentPage"></base-pagination>
    </div>

     <modal v-model:show="showModal">
      <template v-slot:header>
          <h5 class="modal-title" v-if="item.id">Edit analysis request</h5>
          <h5 class="modal-title" v-if="!item.id">Add analysis request</h5>
      </template>
      <form id=item-form role="form" ref="form" @submit.prevent="handleForm(!v$.$invalid)" novalidate>
        <div class="form-group required">
          <label for="sample_name" class="form-label">Sample name</label>
          <base-input 
              id="sample_name"
              v-model="v$.item.sampleName.$model"
              @keydown.space="(event) => event.preventDefault()"
              :valid="isValid(v$.item.sampleName)"
              focused
              required></base-input>
        </div>
        <div class="form-group">
          <label for="item_molecular" class="form-label">Molecular formula</label>
          <input id="item_molecular" 
          type="file" 
          ref="file" 
          class="form-control" 
        :class="[
          { 'is-valid': fileSuccess === true &&  fileError === false },
          { 'is-invalid': fileError }
        ]"
          v-on:change="handleFileUpload()"
          accept="application/pdf,application/x-pdf,image/jpeg,image/jpg,image/png,chemical/cdx,.cdx"/>
          <small class="form-text text-muted">
              PDF, PNG, JPG, CDX max 2Mo
          </small>
          <small class="invalid-feedback" v-if="fileError && fileError !== true">
              {{fileError}} 
          </small>
        </div>
        <div class="form-group required">
          <label for="item_amount_mg" class="form-label">Amount (mg)</label>
          <div class="input-group">
            <input
              id="item_amount_mg"
              type="number"
              step="0.01"
              min="0.01"
              class="form-control"
              v-model.number="v$.item.amountMg.$model"
              :class="[{ 'is-valid': isValid(v$.item.amountMg) },{ 'is-invalid': isValid(v$.item.amountMg) === false }]"
              required>
            <div class="input-group-append">
              <span class="input-group-text">mg</span>
            </div>
          </div>
          <small class="form-text text-muted">Enter a positive number.</small>
        </div>
        <div class="form-group">
          <label for="item_comment" class="form-label">Comment</label>
          <textarea class="form-control"
              id="item_comment"
              v-model="v$.item.comment.$model"
              rows="3"
              :class="[{ 'is-valid': isValid(v$.item.comment) },{ 'is-invalid': isValid(v$.item.comment) === false }]"></textarea>
        </div>
        <div class="form-group required">
          <label for="item_instrument">Instrument</label>
          <select id="item_instrument" 
          @change="deviceOnChange(item.device, true)"
          class="form-control" 
          :class="[{ 'is-valid': isValid(v$.item.device) },{ 'is-invalid': isValid(v$.item.device) === false }]"
          v-model="v$.item.device.$model">
            <option v-for="device in enabledResources(devices)" :key="device.id" v-bind:value="device['@id']">{{device.name}}</option> 
          </select>
        </div>

        <div class="form-group required">
          <label for="item_instrument">Position</label>
          <select id="item_instrument" 
            class="form-control"
            v-model="v$.item.position.$model"
            :class="[{ 'is-valid': isValid(v$.item.position) },{ 'is-invalid': isValid(v$.item.position) === false }]">
            <option v-for="position in 24" v-bind:key="position" v-bind:value="position">{{position}}</option> 
          </select>
        </div>

        <div class="form-group required">
          <label for="item_instrument">Solvent</label>
          <select id="item_instrument" 
            class="form-control" 
            v-model="v$.item.solvent.$model"
            :class="[{ 'is-valid': isValid(v$.item.solvent) },{ 'is-invalid': isValid(v$.item.solvent) === false }]">
            <option v-for="solvent in enabledResources(solvents)" :key="solvent.id" v-bind:value="solvent['@id']">{{solvent.name}}</option> 
          </select>
        </div>

        <div class="form-group required"
        :class="[{ 'is-valid': isValid(v$.item.experiences) },{ 'is-invalid': isValid(v$.item.experiences) === false }]">
           <label>Experiments</label>
            <Multiselect
              v-model="v$.item.experiences.$model"
              mode="tags"
              :searchable="true"
              :createTag="true"
              :close-on-select="false"
              :options="enabledResources(experiences)"
            />
        </div>  
      </form>
      <template v-slot:footer>
          <base-button type="secondary" @click="showModal = false">Close</base-button>
          <base-button v-if="!item.id || (item.user && getUser.id === item.user.id) && item.status == 'draft'" nativeType="submit" form="item-form" @click="item.status = 'draft'">Save as draft</base-button>
          <base-button v-if="!item.id || (item.user && getUser.id === item.user.id)" nativeType="submit" type="primary" @click="item.status = 'submitted'" form="item-form">Submit</base-button>
          <base-button v-if="getUser.isAdmin && item.id && (item.user && getUser.id !== item.user.id)" nativeType="submit" type="primary" @click="item.status = 'submitted'" form="item-form">Save</base-button>
      </template>
    </modal>

    <modal v-model:show="showViewModal">
      <template v-slot:header>
          <h5 class="modal-title">View analysis request</h5>
      </template>

    <div class="items" v-if="showItemData.id">
      <div class="item">
        <b>Sample name:</b>
        <p>
          {{showItemData.sampleName}}
        </p>
      </div>
      <div class="item" v-if="showItemData.file">
        <b>Molecular formula:</b>
        <p>
        <a :href="showItemData.file.contentUrl" target="_blank">View file</a>
        </p>
      </div>

      <div class="item">
        <b>Amount (mg):</b>
        <p>
          <span v-if="showItemData.amountMg !== null && showItemData.amountMg !== undefined">{{ showItemData.amountMg }} mg</span>
          <span v-else class="text-muted">—</span>
        </p>
      </div>

      <div class="item">
        <b>Comment:</b>
        <p>
          {{ showItemData.comment || '—' }}
        </p>
      </div>

      <div class="item">
        <b>Instrument:</b>
        <p>
          {{showItemData.device.name}}
        </p>
      </div>

      <div class="item">
        <b>Position:</b>
        <p>
          {{showItemData.position}}
        </p>
      </div>

      <div class="item">
        <b>Solvent:</b>
        <p>
          {{showItemData.solvent.name}}
        </p>
      </div>

      <div class="item">
        <b>Experiments:</b>
        <div>
          <badge type="success" class="mr-3" v-for="exp in showItemData.experiences" v-bind:key="exp.id">{{exp.name}}</badge>
        </div>
      </div>

      <div class="item">
        <b>Status:</b>
        <badge class="ml-4" :type="itemStatusType(showItemData)">
          <span class="status">{{ itemStatusTypeStr(showItemData) }}</span>
        </badge>
      </div>

    </div>
      <template v-slot:footer>
          <base-button type="secondary" @click="showViewModal = false">Close</base-button>
      </template>
    </modal>
  </div>
</template>
<script>
import moment from 'moment';
import axios from 'axios';
import Multiselect from '@vueform/multiselect'
import useVuelidate from '@vuelidate/core'
import { required, numeric, minValue } from '@vuelidate/validators'
import { mapGetters } from 'vuex'
import { buildExportUrl, validateExportFilters } from '@/utils/exportUrl'

export default {
  name: "analysis-table",
  components: {
    Multiselect,
  },
  setup: () => ({ v$: useVuelidate() }),
  created() {
		this.reloadData();
    this.$resources.devices.list().then(response => (this.devices = response.data));
    this.$resources.solvents.list().then(response => (this.solvents = response.data));
    this.$resources.users.list().then(response => (this.users = response.data));
    this.$resources.groups.list().then(response => (this.groups = response.data));
	},
  data() {
    return {
      items: [],
      showModal: false,
      showViewModal: false,
      item: {},
      showItemData: {}, 
      isSubmitted: false,
      successMessage: false,
      errorMessage: false,
      devices: [],
      experiences: [],
      solvents: [],
      users: [],
      groups: [],
      file: '',
      fileError: false,
      fileSuccess: false,
      currentPage: 1,
      totalItems: 0,
      filters: {
        device: '',
        'order[submittedAt]': 'desc'
      },
      exportFilters: {
        startDate: '',
        endDate: '',
        groupId: ''
      }
    };
  },
  validations() {
    return {
        item: {
            sampleName: {required},
            amountMg: {required, numeric, minValue: minValue(0.01)},
            comment: {},
            device: {required},
            position: {required},
            solvent: {required},
            experiences: {required}
        }
    }
  },
  watch: {
    currentPage() {
      this.reloadData();
    },
    'exportFilters.startDate'() {
      this.reloadData();
    },
    'exportFilters.endDate'() {
      this.reloadData();
    },
    'exportFilters.groupId'() {
      this.reloadData();
    }
  },
  computed: {
      ...mapGetters(['getUser'])
  },
  methods:{
    reloadData() {
      if (this.filters.status == '') {
        this.filters.status = null;
      }

      // Appliquer les filtres de date et groupe à la requête principale
      let combinedFilters = {...this.filters};
      if (this.exportFilters.startDate) {
        combinedFilters.startDate = this.exportFilters.startDate;
      }
      if (this.exportFilters.endDate) {
        combinedFilters.endDate = this.exportFilters.endDate;
      }
      if (this.exportFilters.groupId) {
        combinedFilters.groupId = this.exportFilters.groupId;
      }

      this.$resources.analyses
        .list(this.currentPage, combinedFilters)
        .then(response => {
          this.items = response.data;
          this.totalItems = response.totalItems;
        });    
    },
    sortBy(field) {
      const orderKey = `order[${field}]`;
      const currentOrder = this.filters[orderKey];

      Object.keys(this.filters).forEach(key => {
        if (key.startsWith('order[')) {
          delete this.filters[key];
        }
      });

      this.filters[orderKey] = currentOrder === 'asc' ? 'desc' : 'asc';

      this.reloadData();
    },
    enabledResources(resources) {
      return resources.filter(item => item.enabled)
    },
    deviceOnChange(value, clearExperiences){
      clearExperiences && (this.item.experiences = []); 
      axios.get(value.replace('/api', '') + '/experiences').then (
            resp => {
              this.experiences = resp.data['hydra:member'].map(item => {
                return {
                  value: item['@id'],
                  label: item.name,
                  enabled: item.enabled
                } 
              });
            });
    },
    itemStatusType(item) { 
      switch (item.status){
        case 'draft':
          return 'default';
        case 'submitted':
          return 'info';
        case 'processing':
          return 'primary';
        case 'done':
          return 'success';
        default:
          return 'danger';
      } 

      //return item.status ? 'success' : 'danger';
    } ,
    itemStatusTypeStr(item) {
      return item.status;
    },
    dateFormat(date) {
      if (!date){
        return '-';
      } 

      return moment(String(date)).format('L LTS');
    },
    showEditBtn(item) {
      if (item.status !== 'submitted' && item.status !== 'draft') {
        return false;
      }

      if (this.getUser.isAdmin && item.user.id === this.getUser.id) {
        return true;
      } 

      if (this.getUser.isAdmin && item.status === 'submitted') {
        return true;
      } 

      if (!this.getUser.isAdmin && item.status === 'draft') {
        return true;
      } 

      return false;
    },  
    showEditModal(item) {
      this.fileId = null;
      this.$refs.form.reset();
      this.fileError = false;
      this.fileSuccess = false;
      
      this.item = {...item};
     
      let itemExperiencesArr = []; 
      this.item.experiences.forEach(n => {
        itemExperiencesArr.push(n['@id']);
      });
      this.item.experiences = itemExperiencesArr;
      this.item.device = this.item.device['@id'];  
      this.item.solvent = this.item.solvent['@id'];  
      if (this.item.file){
        this.item.file = this.item.file['@id']; 
      }
      this.deviceOnChange(this.item.device, false);

      this.isSubmitted = false;
      this.showModal = true; 
    },
    showCreateModal() {
      this.isSubmitted = false;
      this.showModal = true;
      this.fileError = false;
      this.fileSuccess = false;
      this.item = {
        enabled: true,
        amountMg: null
      };
    },
    showItem(item) {
      this.showItemData = {...item};
      this.showViewModal = true;
    },
    isValid(item){
        if (!this.isSubmitted){
          return null;
        } 

        return !item.$invalid;
    },
    experiencesClasses(item) {
      let classes = {};

      if (this.isValid(item) === false) {
        classes['container'] = 'multiselect form-control is-invalid';
      }

      return classes;
    },
    handleForm(isValid) {
      this.isSubmitted = true;

      if (!isValid) return;

      if (!this.item.id && this.fileError !== false) {
        return;
      }

      this.saveItem(this.item, true);
    },
    handleFileUpload() {
      this.file = this.$refs.file.files[0];
      if (this.file.size > (1024 * 1024 * 2)) {  // 2M
        this.fileError = 'The file is too large (' + Number.parseFloat((this.file.size / 1024 / 1024)).toFixed(2) + ' MB). Allowed maximum size is 2 MB.'
        this.fileSuccess = false;

        return;
      }

      let formData = new FormData();
      formData.append('file', this.file);

      this.fileError = false;
      this.fileSuccess = false;

      axios.post( '/media_objects',
        formData,
        {
          headers: {
              'Content-Type': 'multipart/form-data'
          }
        }
      ).then(response => { 
        this.fileSuccess = true;
        this.fileError = false;
        this.fileId = response.data['@id'];
      })
      .catch((error) => {

        this.fileSuccess = false;
        this.fileError = true;
        if( error.response && error.response.data){
          if (error.response.data.violations) {
            this.fileError = error.response.data.violations[0].message; 
          } 
        }
        
      });
    },
    saveItem(item, showAlert){
      if (this.fileId) {
        item.file = this.fileId;
      }

      if (item.id) {
          this.$resources.analyses.update(item.id, item).then( (data) => {
              this.reloadData();
              
              showAlert && (this.successMessage = true);
              this.items[this.findIndexById(item.id)] = data;

              this.showModal = false; 
              this.item = {};

          });
      } else {
          this.$resources.analyses.create(this.item).then( (data) => {
              this.items.unshift(data);
              showAlert && (this.successMessage = true);

              this.showModal = false;
              this.item = {};
          });
      }
    },
    cancelAnalysis(item) {
        let data = {};
        data.id = item.id;
        data.status = 'cancelled';

        this.saveItem(data, false);
    },
    doneAnalysis(item) {
        let data = {};
        data.id = item.id;
        data.status = 'done';

        this.saveItem(data, false);
    }, 
    uploadCSV(item) {
      axios
        .get(`/analyses/${item.id}/export.csv`, {
          responseType: 'blob'
        })
        .then(response => {
          let filename = response.headers['content-filename'] ||  item.sampleNameFull + '.csv';

          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();
          this.reloadData();
        })
    }, 
    uploadAllCSV(item) {
        //users/{id}/analyses_{deviceId}.csv
      axios
        .get(`/analyses/${item.id}/export_full.csv`, {
          responseType: 'blob'
        })
        .then(response => {
          let filename = response.headers['content-filename'] ||  item.sampleNameFull + '.csv';

          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();
          this.reloadData();
        })
    }, 
    exportAll() {
      const validationError = validateExportFilters(this.exportFilters);
      if (validationError) {
        this.errorMessage = validationError;
        setTimeout(() => { this.errorMessage = false; }, 3000);
        return;
      }

      const url = buildExportUrl('/analyses/export.csv', this.filters, this.exportFilters);

      axios
        .get(url, {
          responseType: 'blob'
        })
        .then(response => {
          let filename = response.headers['content-filename'] || 'analyses.csv';

          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();

          // Afficher un message de succès simple
          this.successMessage = true;
          setTimeout(() => {
            this.successMessage = false;
          }, 3000);
        })
        .catch(error => {
          console.error('Error exporting analyses:', error);
          // Afficher un message d'erreur simple
          this.errorMessage = 'Erreur lors de l\'export. Veuillez réessayer.';
          setTimeout(() => {
            this.errorMessage = false;
          }, 3000);
        });
    }, 
    findIndexById(id) {
      let index = -1;
      for (let i = 0; i < this.items.length; i++) {
          if (this.items[i].id === id) {
              index = i;
              break;
          }
      }
      return index;
    } 
  }
};
</script>
<style src="@vueform/multiselect/themes/default.css"></style>
<style>
.form-group.required > label:after {
  content:" *";
  color:red;
}

.items .item b {
  font-size: 0.8125rem;
  font-family: inherit;
  font-weight: 600;
} 

.items .item p, .items .item div { 
  display: block;
  margin-bottom: 1rem;
} 

.items .item:last-child p, .items .item:last-child div {
  margin-bottom: 0;
} 

.is-invalid .multiselect {
  border-color: #fb6340;
}

.is-valid .multiselect {
  border-color: #2dce89;
}

th[style*="cursor: pointer"] i {
  margin-left: 5px;
  font-size: 0.7rem;
  color: #5e72e4;
}

th[style*="cursor: pointer"].text-dark i {
  color: #32325d;
}
</style>
